









































































































































































































































































import { Component, Prop, Mixins, Watch, Inject } from "vue-property-decorator";
import { mapState, mapGetters } from "vuex";
import _ from "@/boot/lodash";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import HasValidation from "@/mixins/HasValidation";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import { getNewValues } from "@/helpers/forms";
import {
  ConfigurationModes,
  DeferModes,
  DeferModesValues
} from "@/data/enums/provisioning";
import { LogoSize, LogoType } from "@/data/enums/logos";

// Types
import { IProduct } from "@/models/products";
import {
  IProductProvisioningForm,
  IBlueprintField,
  IProvisionConfiguration,
  IBlueprint,
  IProvisionProvider
} from "@/models/provisioning";
import { IState } from "@/store";
import { IProductProviderData } from "@/models/providers/products";

@Component({
  components: {
    "configure-field-list": () =>
      import(
        "@/components/app/admin/catalogue/products/provisioning/configureFieldList.vue"
      ),
    "configure-setup-functions-list": () =>
      import(
        "@/components/app/admin/catalogue/products/provisioning/configureSetupFunctionsList.vue"
      ),
    "configure-attributes-list": () =>
      import(
        "@/components/app/admin/catalogue/products/provisioning/configureAttributesList.vue"
      ),
    "provision-defer-mode-field": () =>
      import(
        "@/components/app/admin/catalogue/categories/provisionDeferModeField.vue"
      )
  },
  computed: {
    ...mapGetters({
      providersScopeGetter: "data/provisioning/providers/scope",
      providerConfigurationsScopeGetter:
        "data/provisioning/providers/providerConfigurationsScope"
    }),
    ...mapState({
      productScope(state: IState, getters) {
        return getters[`data/${DataModules.CATALOGUE}/scope`](this.productId);
      },
      product(state: IState, getters) {
        return getters[`data/single`]({
          storeModule: DataModules.CATALOGUE,
          scope: this.productScope
        });
      },

      blueprintsScope(state: IState, getters) {
        return getters[`data/${DataModules.PROVISIONING_BLUEPRINTS}/scope`];
      },
      blueprints(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: DataModules.PROVISIONING_BLUEPRINTS,
          scope: this.blueprintsScope
        });
      },

      providers(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: DataModules.PROVISIONING_PROVIDERS,
          scope: this.providersScopeGetter(
            _.get(this.provisionBlueprint, "category_id")
          )
        });
      },

      providerConfigurations(state: IState, getters) {
        return _.orderBy(
          getters[`data/listArray`]({
            storeModule: DataModules.PROVISIONING_PROVIDERS,
            scope: this.providerConfigurationsScopeGetter(
              this.form.provision_provider_id
            )
          }) as IProvisionConfiguration[],
          [config => config.name.toString().toLowerCase()],
          ["asc"]
        );
      },

      productAttributesScope(state: IState, getters) {
        return getters[
          `data/${DataModules.PROVISIONING}/productAttributesScope`
        ](this.productId);
      },
      productAttributes(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: DataModules.PROVISIONING,
          scope: this.productAttributesScope
        });
      }
    })
  }
})
export default class EditProductProvisioning extends Mixins(
  RequestPromises,
  HasValidation
) {
  // Getters
  public providersScopeGetter!: Function;
  public providerConfigurationsScopeGetter!: Function;

  // State
  public product!: IProduct;
  public productScope!: string;
  public blueprints!: IBlueprint[];
  public blueprintsScope!: string;
  public providers!: IProvisionProvider[];
  public providerConfigurations!: IProvisionConfiguration[];
  public productAttributes!: IBlueprintField[];
  public productAttributesScope!: string;

  // Inject
  @Inject({ from: "productProviderData" })
  public readonly $productProviderData!: () => IProductProviderData;

  @Prop({ required: true })
  public productId!: IProduct["id"];

  @Prop({ required: true })
  public configurationsTotal!: number;

  public guardFunctionalities = [
    "staff_get_product",
    "list_provision_blueprints",
    "list_provision_provider_configurations",
    "list_product_provision_configuration_attributes",
    "list_provision_blueprint_fields"
  ];

  public form = {
    provision_blueprint_id: "",
    provision_provider_id: "",
    provision_configuration_id: "",
    provision_configuration_mode: ConfigurationModes.STATIC,
    provision_setup_field_defer_mode: DeferModes.NONE
  } as IProductProvisioningForm;
  public initialForm = {} as IProductProvisioningForm;

  public isLoading = true;
  public isReloading = false;
  public requestError = false;
  public isProcessing = false;
  public isReverting = false;

  public blueprintsLoading = false;
  public providersLoading = false;
  public loadingConfigurations = false;

  public preSelectedConfiguration = "";
  public savedCategoryId = "";
  public hasBrandFields = false;
  public deferModesValues = DeferModesValues();

  // Life-cycle methods

  public created() {
    if (!this.isContentAvailable) return;
    this.loadData();
  }

  public beforeDestroy() {
    this.$store.dispatch("data/bin", {
      storeModule: DataModules.CATALOGUE,
      scope: this.productScope,
      vm: this
    });

    this.$store.dispatch("data/binList", {
      storeModule: DataModules.PROVISIONING,
      scope: this.productAttributesScope,
      vm: this
    });

    this.clearProvidersStore(
      this.providersScopeGetter(this.provisionBlueprint?.category_id)
    );

    this.$store.dispatch("data/binList", {
      storeModule: DataModules.PROVISIONING_BLUEPRINTS,
      scope: this.blueprintsScope,
      vm: this
    });

    this.clearProviderConfigurationsStore(
      this.providerConfigurationsScopeGetter(this.form.provision_provider_id)
    );
  }

  // Methods

  public async loadData() {
    try {
      await Promise.all([
        this.getProductData(),
        this.getBlueprints(),
        this.getProductAttributes()
      ]);

      await this.initiateForm();
      this.requestError = false;
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  public getProductData(ignoreStored = false) {
    return this.$store.dispatch(`data/${DataModules.CATALOGUE}/get`, {
      id: this.productId,
      scope: this.productScope,
      vm: this,
      ignoreStored
    });
  }

  public getBlueprints() {
    return this.$store.dispatch(
      `data/${DataModules.PROVISIONING_BLUEPRINTS}/list`,
      {
        scope: this.blueprintsScope,
        params: { with: "category" },
        vm: this
      }
    );
  }

  public async getProviders(ignoreStored = false) {
    this.providersLoading = true;

    const result = await this.$store.dispatch(
      `data/${DataModules.PROVISIONING_PROVIDERS}/list`,
      {
        categoryId: this.provisionBlueprint?.category_id,
        scope: this.providersScopeGetter(this.provisionBlueprint?.category_id),
        vm: this,
        ignoreStored
      }
    );
    this.providersLoading = false;

    return result;
  }

  public getProviderConfigurations(ignoreStored = false) {
    return this.$store.dispatch(
      `data/${DataModules.PROVISIONING_PROVIDERS}/listProviderConfigurations`,
      {
        categoryId: this.provisionBlueprint?.category_id,
        providerId: this.form.provision_provider_id,
        scope: this.providerConfigurationsScopeGetter(
          this.form.provision_provider_id
        ),
        vm: this,
        ignoreStored
      }
    );
  }

  public getProductAttributes(ignoreStored = false) {
    return this.$store.dispatch(
      `data/${DataModules.PROVISIONING}/listProductAttributes`,
      {
        id: this.productId,
        scope: this.productAttributesScope,
        vm: this,
        params: {
          order: "order",
          limit: 0
        },
        ignoreStored
      }
    );
  }

  public getBrandProvisiongFields() {
    return this.$store.dispatch(
      `data/${DataModules.PROVISIONING_BRAND}/checkBrandFields`,
      {
        blueprintId: this.form.provision_blueprint_id,
        storeData: false,
        returnData: false
      }
    );
  }

  public get hasBlueprintChanged() {
    return (
      this.form.provision_blueprint_id !==
      this.initialForm.provision_blueprint_id
    );
  }

  public get getNewValues() {
    if (!this.hasBlueprintChanged)
      return getNewValues(this.form, this.initialForm);

    return _.pick(this.form, ["provision_blueprint_id"]);
  }

  public get hasBlueprintCategoryId() {
    return !!this.provisionBlueprint?.category_id;
  }

  public get isStaged() {
    return this.$productProviderData().isStaged;
  }

  public get deferModeCV() {
    return _.get(
      this.$productProviderData(),
      "product.category.provision_setup_field_defer_mode",
      null
    );
  }

  public get provisionConfigurationModes() {
    return [
      {
        value: "static",
        label: this.form.provision_provider_id
          ? this.$t("_sentence.provisioning.select_provider_configuration", {
              name: this.providerName
            })
          : this.$t("_.use_named_default", {
              name: this.providerName || this.categoryName
            })
      },
      {
        value: "dynamic",
        label: this.$t("_sentence.provisioning.match_configuration_attributes")
      }
    ];
  }

  public get providerName() {
    if (!this.form.provision_provider_id) return "";

    return _.get(
      _.find(this.availableProviders, ["id", this.form.provision_provider_id]),
      "name",
      ""
    );
  }

  public get categoryName() {
    if (!this.hasBlueprintCategoryId) return "";

    return _.get(
      _.find(this.blueprints, ["id", this.form.provision_blueprint_id]),
      "category.name",
      ""
    );
  }

  public get provisionBlueprint() {
    return _.find(this.blueprints, ["id", this.form.provision_blueprint_id]);
  }

  public getProviderLogoUrl(provider: IProvisionProvider) {
    return _.get(
      _.find(provider.logos || [], logo => {
        if (logo.type === LogoType.ICON && logo.size === LogoSize.ONE_X)
          return true;
        return false;
      }),
      "url",
      null
    );
  }

  public get blueprintProvider() {
    return _.find(this.providers, ["id", this.form.provision_provider_id]);
  }

  public getRoute() {
    return {
      name: "adminBrandProvisioningPreview",
      params: {
        categoryId: this.form.provision_blueprint_id.toString()
      }
    };
  }

  public async revertToInitialState() {
    this.isReverting = true;

    if (this.hasBlueprintChanged) {
      if (this.form.provision_blueprint_id) {
        this.clearProvidersStore(
          this.providersScopeGetter(this.provisionBlueprint?.category_id)
        );
      }

      this.form.provision_blueprint_id = this.initialForm.provision_blueprint_id;
      if (
        this.initialForm.provision_blueprint_id &&
        this.hasBlueprintCategoryId
      ) {
        await this.getProviders(true);
      }
    }

    if (
      this.initialForm.provision_provider_id !== this.form.provision_provider_id
    ) {
      if (this.form.provision_provider_id) {
        this.clearProviderConfigurationsStore(
          this.providerConfigurationsScopeGetter(
            this.form.provision_provider_id
          )
        );
      }

      this.form.provision_provider_id = this.initialForm.provision_provider_id;

      if (this.initialForm.provision_provider_id) {
        this.loadingConfigurations = true;
        await this.getProviderConfigurations(true);
        this.loadingConfigurations = false;
      }
    }

    this.form = _.cloneDeep(this.initialForm);
    this.isReverting = false;
  }

  public get isDynamicMode() {
    return (
      this.form.provision_configuration_mode === ConfigurationModes.DYNAMIC
    );
  }

  public get isFormChanged() {
    return !_.isEqual(this.form, this.initialForm);
  }

  public get attachedProductsIds() {
    return _.map(
      _.concat(
        this.product?.products_options || [],
        this.product?.products_attributes || []
      ),
      product => product.id
    );
  }

  public async initiateForm() {
    // If blueprint selected
    if (this.product?.provision_blueprint_id) {
      this.$set(
        this.form,
        "provision_blueprint_id",
        this.product.provision_blueprint_id
      );

      if (this.hasBlueprintCategoryId) {
        await this.getProviders(true);
        const response = await this.getBrandProvisiongFields();
        this.hasBrandFields = response.total > 0;
      }
    }

    // If provider selected
    if (this.product?.provision_provider_id) {
      this.$set(
        this.form,
        "provision_provider_id",
        this.product.provision_provider_id
      );
      await this.getProviderConfigurations();
    } else {
      this.$set(this.form, "provision_provider_id", "");
    }

    // if configuration selected
    this.$set(
      this.form,
      "provision_configuration_id",
      this.product.provision_configuration_id || ""
    );

    // Set configuration mode
    this.$set(
      this.form,
      "provision_configuration_mode",
      this.product?.provision_configuration_mode
    );

    // Set defer mode
    this.$set(
      this.form,
      "provision_setup_field_defer_mode",
      this.product?.provision_setup_field_defer_mode
    );

    // Set initial form
    this.initialForm = _.cloneDeep(this.form);
  }

  public async validateBeforeSubmit() {
    if (!this.canSave) return;

    try {
      if (!(await this.validate()))
        throw this.$t("_sentence.form_is_not_valid");

      this.isProcessing = true;

      await this.updateProductProvisioning();
      await this.getProductData(true);

      if (
        _.has(this.getNewValues, "provision_blueprint_id") &&
        this.form.provision_blueprint_id
      ) {
        await this.getProductAttributes(true);
      }

      await this.initiateForm();

      this.$store.dispatch("toast/show", {
        message: this.$t(
          "_sentence.provisioning.product_provisioning_successfully_updated"
        )
      });
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    } finally {
      this.isProcessing = false;
    }
  }

  public updateProductProvisioning() {
    return this.$store.dispatch(`data/${DataModules.CATALOGUE}/update`, {
      id: this.productId,
      scope: this.productScope,
      updateStore: true,
      data: this.getNewValues
    });
  }

  public async onConfigurationChange(value: any) {
    if (value !== "new") {
      return;
    }

    this.$nextTick(() => {
      this.form.provision_configuration_id = this.preSelectedConfiguration;
    });

    this.$store.dispatch(
      `data/${DataModules.PROVISIONING_CONFIGURATIONS}/openManageProviderConfigurationModal`,
      {
        total: this.configurationsTotal,
        props: {
          categoryId: this.provisionBlueprint?.category_id,
          providerId: this.form.provision_provider_id
        },
        onCancel: () => {
          this.isLoading = false;
        },
        events: {
          success: async result => {
            await this.getProviderConfigurations(true);
            this.form.provision_configuration_id = result.id;
          }
        }
      }
    );
  }

  public clearProvidersStore(providersScope: string) {
    this.$store.dispatch("data/binList", {
      storeModule: DataModules.PROVISIONING_PROVIDERS,
      scope: providersScope,
      vm: this
    });
  }

  public clearProviderConfigurationsStore(providerConfigurationsScope: string) {
    this.$store.dispatch("data/binList", {
      storeModule: DataModules.PROVISIONING_PROVIDERS,
      scope: providerConfigurationsScope,
      vm: this
    });
  }

  public clearSetupFunctionsStore(setupFunctionsScope: string) {
    this.$store.dispatch("data/binList", {
      storeModule: DataModules.PROVISIONING_BLUEPRINTS,
      scope: setupFunctionsScope,
      vm: this
    });
  }

  // Computed

  public get availableProviders() {
    return _.concat(this.providers, [
      {
        name: this.$t("_.auto_mode").toString(),
        id: ""
      } as IProvisionProvider
    ]);
  }

  // Watchers

  @Watch("form.provision_provider_id")
  public async onProvisionProviderChange(newValue, oldValue) {
    if (this.isReverting) return;

    try {
      // Clear previous provider configuraion
      this.form.provision_configuration_id = "";

      if (oldValue) {
        this.clearProviderConfigurationsStore(
          this.providerConfigurationsScopeGetter(oldValue)
        );
      }

      // Load new provider configuration
      if (newValue) {
        this.loadingConfigurations = true;
        await this.getProviderConfigurations(true);
        this.loadingConfigurations = false;
      }
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    }
  }

  @Watch("form.provision_configuration_id")
  public onProvisionConfigurationChange(value) {
    if (value === "new" || this.isReverting) {
      return;
    }
    this.preSelectedConfiguration = this.form.provision_configuration_id;
  }

  public get canSave() {
    return this.$userCan("update_product") && !this.isStaged;
  }

  public get isContentAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }
}
